import { Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { EngagementService } from 'src/app/care/engagement/engagement.service';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { WhatsappTemplateButtonTypeEnum } from 'src/app/parameters/message-templates/whatsapp-templates/shared/models/whatsapp-template-button-type.enum';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { ChooseTemplateFlowService } from 'src/app/shared/components/choose-template-flow/shared/choose-template-flow.service';
import { EngagementTemplateFlowStepEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-template-flow-step.enum';
import { EditorConfig } from 'src/app/shared/editor-config';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { AttachmentMediaTypeEnum, AttachmentType } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { WhatsappTemplate } from 'src/app/task-process/shared/models';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { environment } from 'src/environments/environment';

import {
  KakaotalkTemplate,
  KakaotalkTemplateButtonTypeEnum,
} from 'src/app/parameters/message-templates/kakaotalk-templates/shared';
import { UploadService } from 'src/app/shared/services/upload.service';
import { AgentShortcutService } from '../../agent-shortcuts-modal/agent-shortcut.service';
import { Choice } from '../../select/choice.model';

@Component({
  selector: 'app-compose-message',
  templateUrl: './compose-message.component.html',
  styleUrls: [
    '../../../../task-process/answer-editor/answer-editor.component.scss',
    './compose-message.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ComposeMessageComponent implements OnDestroy {
  isExpanded = true;
  subscriptions: Subscription = new Subscription();

  AttachmentMediaTypeEnum = AttachmentMediaTypeEnum;
  uploading: boolean;
  uploadProgress$: BehaviorSubject<number> = new BehaviorSubject(0);
  supportedMediaTypes: string;
  uploadMediaTypeLabel: string;
  editMediaTypeLabel: string;
  deleteMediaTypeLabel: string;
  inputsPreviewButtonsHeight = 300;
  isWebMode = environment.bundledWithAngularJSApp;

  agentShortcuts: Choice[] = [];
  hasUrl: boolean;
  urlButtonsFormArray = new FormArray([]);

  hasQuickReplies: boolean;
  quickReplyButtonsFormArray = new FormArray([]);

  hasCopyCode: boolean;
  copyCodeButtonFormControl = new FormControl(null);
  couponCodePattern = /^[a-z0-9]{1,15}$/i;

  EngagementTemplateFlowStepEnum = EngagementTemplateFlowStepEnum;
  SiteTypeEnum = SiteTypeEnum;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  get taskProcessContext(): TaskProcessContext {
    return this.chooseTemplateFlowService.taskProcessContext;
  }

  get selectedTemplate(): any {
    return this.chooseTemplateFlowService.selectedTemplate;
  }

  get selectedWhatsappTemplate(): WhatsappTemplate {
    return this.chooseTemplateFlowService.selectedTemplate as WhatsappTemplate;
  }

  get selectedKakaotalkTemplate(): KakaotalkTemplate {
    return this.chooseTemplateFlowService.selectedTemplate as KakaotalkTemplate;
  }

  constructor(
    public chooseTemplateFlowService: ChooseTemplateFlowService,
    public engagementService: EngagementService,
    private elementRef: ElementRef,
    private basicService: BasicService,
    private whatsappTemplatesService: WhatsappTemplatesService,
    private errorHandlingService: ErrorHandlingService,
    private alcmeonContextService: AlcmeonContextService,
    private agentShortcutService: AgentShortcutService,
    private uploadService: UploadService
  ) {
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        this.initializeWhatsappComposer();
        break;
      case SiteTypeEnum.Kakaotalk:
        this.initializeKakaotalkComposer();
        break;
      default:
        this.initializeWhatsappComposer();
    }
  }

  changeRecipient() {
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_RECIPIENT_SENDER;
  }

  changeTemplate() {
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_TEMPLATE_MESSAGE;
  }

  uploadMedia($event) {
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        this.uploadMediaForWhatsapp($event);
        break;
      case SiteTypeEnum.Kakaotalk:
        this.uploadMediaForKakaotalk($event);
        break;
    }
  }

  deleteMedia() {
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        this.deleteMediaForWhatsapp();
        break;
      case SiteTypeEnum.Kakaotalk:
        this.deleteMediaForKakaotalk();
        break;
    }
  }

  checkMessageValidity() {
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        this.checkWhatsappMessageValidity();
        break;
      case SiteTypeEnum.Kakaotalk:
        this.checkKakaotalkMessageValidity();
        break;
    }
  }

  private checkWhatsappMessageValidity() {
    if (!this.selectedWhatsappTemplate) {
      return false;
    }
    this.selectedWhatsappTemplate.preview = this.getPreview(this.selectedWhatsappTemplate, false);
    let hasHeaderValid = true;
    if (this.selectedWhatsappTemplate.header) {
      if (this.selectedWhatsappTemplate.header.type === 'text') {
        hasHeaderValid = !!this.selectedWhatsappTemplate.header.text;
      } else {
        const { media_url } = this.selectedWhatsappTemplate.header;
        hasHeaderValid = !!media_url;
      }
    }
    const form: FormGroup = new FormGroup({
      header: new FormGroup({
        text: new FormControl(),
        type: new FormControl(),
        media_type: new FormControl(),
        media_url: new FormControl(),
        filename: new FormControl(),
      }),
      body: new FormControl({}),
      buttons: new FormArray([]),
      footer: new FormControl(),
    });

    const { header, preview, footer, buttons } = this.selectedWhatsappTemplate;
    (buttons || []).forEach(button => {
      if (
        [WhatsappTemplateButtonTypeEnum.COPY_CODE, WhatsappTemplateButtonTypeEnum.URL].includes(button.type) &&
        !Array.isArray(button.example)
      ) {
        button.example = [button.example];
      }
      const buttonForm = new FormGroup({
        text: new FormControl(),
        type: new FormControl(),
        url: new FormControl(),
        example: new FormArray([]),
      });
      buttonForm.patchValue(button);
      (buttonForm.get('example') as FormArray).push(new FormControl(button.example ? button.example[0] : ''));
      (form.get('buttons') as FormArray).push(buttonForm);
    });

    form.patchValue({ header, body: preview, footer });

    this.chooseTemplateFlowService.templateDisplayedBS.next(form);

    setTimeout(() => {
      if (this.elementRef.nativeElement.querySelector('app-whatsapp-template-preview')?.offsetHeight) {
        this.inputsPreviewButtonsHeight = this.elementRef.nativeElement.querySelector(
          'app-whatsapp-template-preview'
        ).offsetHeight;
      }
    }, 0);

    const allParametersAreValid = this.selectedWhatsappTemplate.parameters.every(parameter => !!parameter.value);
    const allButtonsAreValid = this.checkWhatsappButtonsValidity();
    this.chooseTemplateFlowService.isValid = hasHeaderValid && allParametersAreValid && allButtonsAreValid;
  }

  private uploadMediaForWhatsapp($event) {
    const file = $event.target.files?.item(0);
    const { header } = this.selectedWhatsappTemplate;

    if (header) {
      const { media_type } = header;
      const editor = new EditorConfig(SiteTypeEnum.WhatsApp, {
        isPrivate: true,
        isEngagement: this.taskProcessContext.engagement,
      });
      // Specifically for this use case, overrides because only pdf files are accepted.
      editor.fileMimeTypes = 'application/pdf';
      try {
        editor.checkLimitations(media_type, file);
      } catch ({ translationPath, translationData }) {
        this.basicService.presentAlert({
          subHeader: `${this.basicService.translate(translationPath, translationData)}`,
          buttons: [this.basicService.translate('m/ok')],
        });
        return;
      }

      // The picture is valid for WhatsApp in private then we can try to upload this one.
      this.uploading = true;
      this.uploadProgress$.next(0);

      try {
        this.selectedWhatsappTemplate.header.filename = file.name || this.basicService.translate('attachments/File');

        this.whatsappTemplatesService
          .uploadMediaForMessage(this.taskProcessContext.site.id, file, media_type)
          .pipe(
            this.errorHandlingService.catchAlertAndThrowHttpError(),
            finalize(() => {
              this.uploading = false;
              this.fileInput = null;
              // SP-5133
              // this.headerCustomFieldCtrl.reset();
              this.checkWhatsappMessageValidity();
            })
          )
          .subscribe({
            next: ({ headerMediaUrl, headerMediaFilename }) => {
              this.selectedWhatsappTemplate.header.media_url = headerMediaUrl;
              this.selectedWhatsappTemplate.header.filename = headerMediaFilename;
            },
          });
      } catch (e) {
        // Do nothing in case of issue.
      }
    }
  }

  private deleteMediaForWhatsapp() {
    if (this.fileInput?.nativeElement?.value) {
      this.fileInput.nativeElement.value = '';
    }
    this.selectedWhatsappTemplate.header.media_url = null;
    this.checkWhatsappMessageValidity();
  }

  private payloadChangedForWhatsapp(payloads) {
    payloads.forEach(
      (payload: string, index: number) => (this.selectedWhatsappTemplate.quickReplyButtons[index].payload = payload)
    );

    this.checkWhatsappMessageValidity();
  }

  private urlSuffixChangedForWhatsapp(values) {
    (values || []).forEach(
      (value: string, index: number) => (this.selectedWhatsappTemplate.urlButtons[index].example = [value])
    );
    this.checkWhatsappMessageValidity();
  }

  private urlSuffixChangedForKakaotalk(values) {
    (values || []).forEach((value: string, index: number) => {
      const { url } = this.selectedWhatsappTemplate.urlButtons[index];
      this.selectedWhatsappTemplate.urlButtons[index]['urlFormatted'] = url.replace('{{1}}', value);
    });
    this.checkKakaotalkMessageValidity();
  }

  private initializeWhatsappComposer() {
    this.checkWhatsappCopyCodeButton();
    this.checkWhatsappQuickReplyButtons();
    this.checkWhatsappUrlButtons();
    this.getWhatsappSupportedMediaTypes();
    this.checkWhatsappMessageValidity();
  }

  private checkWhatsappCopyCodeButton() {
    this.hasCopyCode = (this.selectedWhatsappTemplate.buttons || []).some(
      ({ type }) => type === WhatsappTemplateButtonTypeEnum.COPY_CODE
    );
    if (this.hasCopyCode) {
      this.copyCodeButtonFormControl = new FormControl(null, [
        Validators.required,
        Validators.pattern(this.couponCodePattern),
      ]);
      this.subscriptions.add(
        this.copyCodeButtonFormControl.valueChanges.subscribe(value => {
          if (!this.selectedWhatsappTemplate.copyCodeButtons) {
            this.selectedWhatsappTemplate.copyCodeButtons = [];
          }
          this.selectedWhatsappTemplate.copyCodeButtons[0].example = [value];
        })
      );
      this.checkWhatsappMessageValidity();
    }
  }

  private checkWhatsappQuickReplyButtons() {
    this.hasQuickReplies = (this.selectedWhatsappTemplate.buttons || []).some(
      ({ type }) => type === WhatsappTemplateButtonTypeEnum.QUICK_REPLY
    );
    if (this.hasQuickReplies) {
      this.quickReplyButtonsFormArray = new FormArray([]);
      this.agentShortcutService
        .getAgentShortcuts(this.alcmeonContextService.companyId, this.taskProcessContext.site.id)
        .subscribe({
          next: agentShortcuts => {
            this.agentShortcuts = agentShortcuts.map(({ element_id, label }) => ({ id: element_id, label }));
            (this.selectedWhatsappTemplate.buttons || [])
              .filter(({ type }) => type === WhatsappTemplateButtonTypeEnum.QUICK_REPLY)
              .forEach(({ payload }) =>
                this.quickReplyButtonsFormArray.push(new FormControl({ value: payload ?? '', disabled: !!payload }))
              );
            this.subscriptions.add(
              this.quickReplyButtonsFormArray.valueChanges.subscribe(() =>
                this.payloadChangedForWhatsapp(this.quickReplyButtonsFormArray.getRawValue())
              )
            );
            this.checkWhatsappMessageValidity();
          },
        });
    }
  }

  private checkWhatsappUrlButtons() {
    this.hasUrl = (this.selectedWhatsappTemplate.buttons || []).some(
      ({ type, url, example }) =>
        type === WhatsappTemplateButtonTypeEnum.URL && url.includes('{{1}}') && example?.length
    );
    if (this.hasUrl) {
      this.urlButtonsFormArray = new FormArray([]);
      (this.selectedWhatsappTemplate.buttons || [])
        .filter(
          ({ type, url, example }) =>
            type === WhatsappTemplateButtonTypeEnum.URL && url.includes('{{1}}') && example?.length
        )
        .forEach(() => this.urlButtonsFormArray.push(new FormControl('')));
      this.subscriptions.add(
        this.urlButtonsFormArray.valueChanges.subscribe(() =>
          this.urlSuffixChangedForWhatsapp(this.urlButtonsFormArray.getRawValue())
        )
      );
      this.checkWhatsappMessageValidity();
    }
  }

  private checkWhatsappButtonsValidity() {
    let allButtonsAreValid = false;
    if (!this.hasQuickReplies && !this.hasCopyCode && !this.hasUrl) {
      allButtonsAreValid = true;
    } else {
      const quickReplyButtonsFormArray = this.quickReplyButtonsFormArray.getRawValue();
      const urlButtonsFormArray = this.urlButtonsFormArray.getRawValue();
      const quickReplyButtonsValid =
        !this.hasQuickReplies ||
        ((this.quickReplyButtonsFormArray.disabled || this.quickReplyButtonsFormArray.valid) &&
          !!quickReplyButtonsFormArray.length &&
          quickReplyButtonsFormArray.every(v => !!v));
      const copyCodeButtonsValid = !this.hasCopyCode || this.copyCodeButtonFormControl.valid;
      const urlButtonsValid =
        !this.hasUrl ||
        ((this.urlButtonsFormArray.disabled || this.urlButtonsFormArray.valid) &&
          !!urlButtonsFormArray.length &&
          urlButtonsFormArray.every(v => !!v));

      allButtonsAreValid = quickReplyButtonsValid && copyCodeButtonsValid && urlButtonsValid;
    }
    return allButtonsAreValid;
  }

  /**
   * Depends on the header media type defined in the WA BSM, the supported types are either image or pdf.
   */
  private getWhatsappSupportedMediaTypes() {
    if (!this.selectedWhatsappTemplate.header) {
      return;
    }
    switch (this.selectedWhatsappTemplate.header.media_type) {
      case AttachmentMediaTypeEnum.Image:
        this.supportedMediaTypes = 'image/jpeg,image/png';
        this.uploadMediaTypeLabel = this.basicService.translate('m/task-process/button/add-image');
        this.editMediaTypeLabel = this.basicService.translate('m/task-process/button/replace-image');
        this.deleteMediaTypeLabel = this.basicService.translate('m/task-process/button/delete-image');
        break;
      case AttachmentMediaTypeEnum.Document:
        this.supportedMediaTypes = 'application/pdf';
        this.uploadMediaTypeLabel = this.basicService.translate('m/task-process/button/add-document');
        this.editMediaTypeLabel = this.basicService.translate('m/task-process/button/replace-document');
        this.deleteMediaTypeLabel = this.basicService.translate('m/task-process/button/delete-document');
        break;
      case AttachmentMediaTypeEnum.Video:
        this.supportedMediaTypes = 'video/mp4,video/3gpp';
        this.uploadMediaTypeLabel = this.basicService.translate('m/task-process/button/add-video');
        this.editMediaTypeLabel = this.basicService.translate('m/task-process/button/replace-video');
        this.deleteMediaTypeLabel = this.basicService.translate('m/task-process/button/delete-video');
        break;
      default:
        break;
    }
  }

  private initializeKakaotalkComposer() {
    this.checkKakaotalkUrlButtons();
    this.getKakaotalkSupportedMediaTypes();
    this.checkKakaotalkMessageValidity();
  }

  private checkKakaotalkUrlButtons() {
    this.hasUrl = (this.selectedKakaotalkTemplate.buttons || []).some(
      ({ type, url, example }) =>
        type === KakaotalkTemplateButtonTypeEnum.URL && url.includes('{{1}}') && example?.length
    );
    if (this.hasUrl) {
      this.urlButtonsFormArray = new FormArray([]);
      (this.selectedKakaotalkTemplate.buttons || [])
        .filter(
          ({ type, url, example }) =>
            type === KakaotalkTemplateButtonTypeEnum.URL && url.includes('{{1}}') && example?.length
        )
        .forEach(() => this.urlButtonsFormArray.push(new FormControl('')));
      this.subscriptions.add(
        this.urlButtonsFormArray.valueChanges.subscribe(() =>
          this.urlSuffixChangedForKakaotalk(this.urlButtonsFormArray.getRawValue())
        )
      );
      this.checkKakaotalkMessageValidity();
    }
  }

  private checkKakaotalkButtonsValidity() {
    let allButtonsAreValid = false;
    if (!this.hasUrl) {
      allButtonsAreValid = true;
    } else {
      const urlButtonsFormArray = this.urlButtonsFormArray.getRawValue();
      allButtonsAreValid =
        !this.hasUrl ||
        ((this.urlButtonsFormArray.disabled || this.urlButtonsFormArray.valid) &&
          !!urlButtonsFormArray.length &&
          urlButtonsFormArray.every(v => !!v));
    }
    return allButtonsAreValid;
  }

  private getKakaotalkSupportedMediaTypes() {
    if (!this.selectedKakaotalkTemplate.header) {
      return;
    }
    switch (this.selectedKakaotalkTemplate.header.media_type) {
      case AttachmentType.Image:
        this.supportedMediaTypes = 'image/jpeg,image/png';
        this.uploadMediaTypeLabel = this.basicService.translate('m/task-process/button/add-image');
        this.editMediaTypeLabel = this.basicService.translate('m/task-process/button/replace-image');
        this.deleteMediaTypeLabel = this.basicService.translate('m/task-process/button/delete-image');
        break;
      default:
        break;
    }
  }

  private checkKakaotalkMessageValidity() {
    if (!this.selectedKakaotalkTemplate) {
      return false;
    }
    this.selectedKakaotalkTemplate.preview = this.getPreview(this.selectedKakaotalkTemplate, false);
    let hasHeaderValid = true;
    if (this.selectedKakaotalkTemplate.header?.media_type) {
      const { media_id } = this.selectedKakaotalkTemplate.header;
      hasHeaderValid = !!media_id;
    }
    const form: FormGroup = new FormGroup({
      header: new FormGroup({
        type: new FormControl(),
        media_type: new FormControl(),
        media_url: new FormControl(),
        filename: new FormControl(),
      }),
      body: new FormControl({}),
      buttons: new FormArray([]),
    });

    const { header, preview, buttons } = this.selectedKakaotalkTemplate;

    (buttons || []).forEach(button => {
      if (KakaotalkTemplateButtonTypeEnum.URL === button.type && !Array.isArray(button.example)) {
        button.example = [button.example];
      }
      const buttonForm = new FormGroup({
        text: new FormControl(),
        type: new FormControl(),
        url: new FormControl(),
        example: new FormArray([]),
      });
      buttonForm.patchValue(button);
      (buttonForm.get('example') as FormArray).push(new FormControl(button.example ? button.example[0] : ''));
      (form.get('buttons') as FormArray).push(buttonForm);
    });

    form.patchValue({ header, body: preview });

    this.chooseTemplateFlowService.templateDisplayedBS.next(form);

    setTimeout(() => {
      if (this.elementRef.nativeElement.querySelector('app-kakaotalk-template-preview')?.offsetHeight) {
        this.inputsPreviewButtonsHeight = this.elementRef.nativeElement.querySelector(
          'app-kakaotalk-template-preview'
        ).offsetHeight;
      }
    }, 0);

    const allParametersAreValid = this.selectedKakaotalkTemplate.parameters.every(parameter => !!parameter.value);
    const allButtonsAreValid = this.checkKakaotalkButtonsValidity();
    this.chooseTemplateFlowService.isValid = hasHeaderValid && allParametersAreValid && allButtonsAreValid;
  }

  private uploadMediaForKakaotalk($event) {
    const file = $event.target.files?.item(0);
    const { header } = this.selectedKakaotalkTemplate;

    if (header) {
      const editor = new EditorConfig(SiteTypeEnum.Kakaotalk, {
        isPrivate: true,
        isEngagement: this.taskProcessContext.engagement,
      });
      try {
        editor.checkLimitations(AttachmentMediaTypeEnum.Image, file);
      } catch ({ translationPath, translationData }) {
        this.basicService.presentAlert({
          subHeader: `${this.basicService.translate(translationPath, translationData)}`,
          buttons: [this.basicService.translate('m/ok')],
        });
        return;
      }

      this.basicService.showSpinner();
      this.uploadProgress$.next(0);

      try {
        this.uploadService
          .uploadPictureAsync(file, this.taskProcessContext.site.id)
          .pipe(
            this.errorHandlingService.catchAlertAndThrowHttpError(),
            finalize(() => {
              this.basicService.hideSpinner();
              this.fileInput = null;
              this.checkKakaotalkMessageValidity();
            })
          )
          .subscribe({
            next: ({ media_id }) => {
              this.selectedKakaotalkTemplate.header.media_id = media_id;
              this.selectedKakaotalkTemplate.header.media_url = this.uploadService.pictureUrl(media_id);
            },
          });
      } catch (e) {
        // Do nothing in case of issue.
      }
    }
  }

  private deleteMediaForKakaotalk() {
    console.error('Not implemented');
  }

  private getPreview(template: any, forcePlaceholder: boolean): string {
    let result = template.text || template.body;
    let index = 1;
    for (const parameter of template.parameters) {
      const placeholder = `<${parameter.placeholder?.label || parameter.placeholder}>`;
      const replacement = forcePlaceholder ? placeholder : parameter.value || placeholder;
      result = result.replace(`{{${index++}}}`, replacement);
    }
    return result;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
